import {
  Editor,
  JSONContent,
  TextSerializer,
  getTextSerializersFromSchema,
} from '@tiptap/core'
import { Node } from '@tiptap/pm/model'

export type ContentChain = {
  json: JSONContent
  html: string
  text: string
}

/**
 * This is a fork of Tiptap's own `getText` method.
 * Their implementation helpfully has a feature that skips consecutive
 * blank nodes when serializing to text, but this isn't desirable for
 * our use case. This version omits that feature.
 */
const getText = (
  startNode: Node,
  textSerializers: Record<string, TextSerializer>,
  blockSeparator = '\n'
) => {
  let text = ''
  let separated = true
  const from = 0
  const to = startNode.content.size

  startNode.nodesBetween(from, to, (node, pos, parent, index) => {
    const textSerializer = textSerializers?.[node.type.name]
    if (textSerializer) {
      if (node.isBlock) {
        text += blockSeparator
      }
      if (parent) {
        text += textSerializer({
          node,
          pos,
          parent,
          index,
          range: { from, to },
        })
      }
    } else if (node.isText) {
      text += node?.text?.slice(Math.max(from, pos) - pos, to - pos)
      separated = false
    } else if (node.isBlock && !separated) {
      text += blockSeparator
    }
  })

  return text
}

export const getContentChain = (editor: Editor): ContentChain => {
  return new (class {
    get json() {
      return editor.getJSON()
    }
    get html() {
      return editor.getHTML()
    }
    get text() {
      const textSerializers = getTextSerializersFromSchema(editor.schema)
      return getText(editor.state.doc, textSerializers)
    }
  })()
}

export default getContentChain
